import { useEffect, useState } from 'react'
import { ChevronDownIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { IResolvedParticipant } from '../../../Model/Project/IResolvedParticipant.js'
import type { IEvaluationProject } from '../../../Model/Project/IEvaluationProject.js'
import type { IUser } from '../../../Model/index.js'
import { ProjectParticipantBadge } from './ProjectParticipantBadge.js'
import type { IProjectNote } from '../../../Model/Project/IProjectNote.js'
import { useDispatch, useSelector } from 'react-redux'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import { selectNotesByProjectId } from '../../../Flux/Selector/index.js'
import { DateWidget } from '../../Common/DateWidget.js'

type ProjectNoteWidgetProps = {
  project: IEvaluationProject
  participants: IResolvedParticipant[] | 'LOADING'
  currentUser?: IUser
  readOnly: boolean
}

export const ProjectNoteWidget = ({ project, participants, currentUser, readOnly }: ProjectNoteWidgetProps) => {
  const dispatch = useDispatch()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [newNoteContent, setNewNoteContent] = useState('')
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [editingNoteContent, setEditingNoteContent] = useState('')

  // Get notes from Redux store
  const notes = useSelector(selectNotesByProjectId(project?.id || '')) || []

  // Load notes when component mounts or project changes
  useEffect(() => {
    if (project?.id) {
      setIsLoading(true)
      dispatch(projectSlice.actions.getNotesRequest({ projectId: project.id }))
      // Set loading to false after a short delay to allow the notes to load
      const timer = setTimeout(() => setIsLoading(false), 500)
      return () => clearTimeout(timer)
    }
  }, [project?.id, dispatch])

  // Find participant by user ID
  const findParticipant = (userId: string) => {
    if (participants === 'LOADING' || !participants) return null
    return participants.find((p) => p.participant.userId === userId)
  }

  // Check if the current user can delete a note
  const canDeleteNote = (note: IProjectNote) => {
    if (readOnly) return false
    if (!currentUser) return false
    return note.creator === currentUser.id || project.ownerUser === currentUser.id
  }

  // Check if the current user can edit a note
  const canEditNote = (note: IProjectNote) => {
    if (readOnly) return false
    if (!currentUser) return false
    return note.creator === currentUser.id
  }

  // Handle adding a new note
  const handleAddNote = () => {
    if (!newNoteContent.trim() || !project?.id || !currentUser) return

    dispatch(
      projectSlice.actions.addNoteRequest({
        projectId: project.id,
        note: {
          projectId: project.id,
          content: newNoteContent,
          creator: currentUser.id,
          deletedAt: null
        }
      })
    )
    setNewNoteContent('')
  }

  // Handle deleting a note
  const handleDeleteNote = (noteId: number) => {
    if (!project?.id) return

    dispatch(
      projectSlice.actions.deleteNoteRequest({
        projectId: project.id,
        noteId
      })
    )
  }

  // Start editing a note
  const startEditingNote = (note: IProjectNote) => {
    if (note.id === null) return
    setEditingNoteId(note.id)
    setEditingNoteContent(note.content)
  }

  // Cancel editing a note
  const cancelEditingNote = () => {
    setEditingNoteId(null)
    setEditingNoteContent('')
  }

  // Save edited note
  const saveEditedNote = () => {
    if (!editingNoteContent.trim() || !project?.id || editingNoteId === null) {
      cancelEditingNote()
      return
    }

    dispatch(
      projectSlice.actions.updateNoteRequest({
        projectId: project.id,
        noteId: editingNoteId,
        content: editingNoteContent
      })
    )
    cancelEditingNote()
  }

  return (
    <div className="mb-6">
      {/* Collapsible Header */}
      <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg shadow-md">
        <div data-testid="note-collapsible" className="flex items-center cursor-pointer grow" onClick={() => setIsExpanded(!isExpanded)}>
          <h2 className="text-lg font-semibold">Notes</h2>
          <div className="flex mx-10 gap-2 w-full">
            {notes.length > 0 ? (
              notes.slice(0, 3).map((note) => {
                const participant = findParticipant(note.creator)
                return participant?.user ? (
                  <ProjectParticipantBadge mayEdit={participant.participant.mayEdit} user={participant.user} key={note.id} />
                ) : null
              })
            ) : (
              <span className="text-gray-500 italic">No notes yet</span>
            )}
            {notes.length > 3 && <span className="text-gray-500">+{notes.length - 3} more</span>}
          </div>
          <div>
            <ChevronDownIcon className={`w-4 h-4 transform transition-all ${isExpanded && 'rotate-180'}`} />
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-3 bg-white p-4 rounded-lg shadow-sm">
          {isLoading ? (
            <div className="text-center py-4">Loading notes...</div>
          ) : (
            <>
              {/* Notes List */}
              <div className="space-y-4 mb-6">
                {notes.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 italic">No notes yet. Add one below!</div>
                ) : (
                  notes.map((note) => {
                    const participant = findParticipant(note.creator)
                    return (
                      <div data-testid="note-container" key={note.id} className="border rounded-lg p-3 relative">
                        <div className="flex items-center mb-2">
                          {participant?.user && (
                            <div className="mr-2">
                              <ProjectParticipantBadge mayEdit={participant.participant.mayEdit} user={participant.user} />
                            </div>
                          )}
                          <div>
                            <div className="font-semibold">
                              {participant?.user ? `${participant.user.email} (${participant.user.org.name})` : 'Unknown User'}
                            </div>
                            <div className="text-xs text-gray-500"><DateWidget date={note.createdAt} /></div>
                          </div>
                          {canDeleteNote(note) && (
                            <button
                              data-testid={"delete-note"}
                              className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                              onClick={() => note.id !== null && handleDeleteNote(note.id)}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                          {canEditNote(note) && (
                            <button
                              data-testid={"edit-note"}
                              className="absolute top-2 right-8 text-blue-500 hover:text-blue-700"
                              onClick={() => startEditingNote(note)}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {editingNoteId === note.id ? (
                          <div>
                            <textarea
                              data-testid={"edit-note-text"}
                              className="w-full p-2 border rounded-sm"
                              value={editingNoteContent}
                              onChange={(e) => setEditingNoteContent(e.target.value)}
                              rows={3}
                            />
                            <div className="flex justify-end mt-2 space-x-2">
                              <button className="btn btn-sm btn-outline" onClick={cancelEditingNote}>
                                Cancel
                              </button>
                              <button data-testid="edit-note-submit" className="btn btn-sm btn-primary" onClick={saveEditedNote}>
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap">{note.content}</div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>

              {/* Add Note Form */}
              {!readOnly && (
                <div className="border-t pt-4">
                  <textarea
                    data-testid={'add-note-text'}
                    className="w-full p-2 border rounded-sm mb-2"
                    placeholder="Type your note here..."
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    rows={3}
                  />
                  <div className="flex justify-end">
                    <button data-testid={"add-note-btn"} className="btn btn-primary" onClick={handleAddNote} disabled={!newNoteContent.trim()}>
                      Add Note
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
